import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { FieldError } from "react-hook-form";

import FormInputLabel from "./FormInputLabel";
import { isSameCalendarDay, startOfDay } from "../utils/dateFormatters";

export type FormDateTimeInputProps = {
    id: string,
    label: string
    value: Date | null
    onChange: (value: Date | null) => void
    error?: FieldError
    minDate?: Date
    maxDate?: Date
    required?: boolean
    hideHelpText?: boolean
    helperText?: string
    tooltip?: string
}

const FormDateTimeInput = ({
    id,
    label,
    value,
    onChange,
    error,
    minDate,
    maxDate,
    required,
    hideHelpText,
    helperText = "day/month/year HH:mm aa",
    tooltip
}: FormDateTimeInputProps) => {
    const pickerMinDate = minDate ? startOfDay(minDate) : undefined;
    const referenceDate = pickerMinDate ?? startOfDay(new Date());

    const handleChange = (newValue: Date | null) => {
        if (!newValue) {
            onChange(null);
            return;
        }

        if (!value || !isSameCalendarDay(value, newValue)) {
            onChange(startOfDay(newValue));
            return;
        }

        onChange(newValue);
    };

    return (
        <div className="w-full space-y-2">
            <FormInputLabel tooltipText={tooltip} htmlFor={id} required={required}>{label}</FormInputLabel>
            <DateTimePicker
                format="dd/MM/yy hh:mm aa"
                minutesStep={5}
                ampm
                value={value}
                referenceDate={referenceDate}
                onOpen={() => {
                    if (!value && pickerMinDate) {
                        onChange(pickerMinDate);
                    }
                }}
                onChange={handleChange}
                minDate={pickerMinDate}
                maxDate={maxDate}
                slotProps={{
                    textField: {
                        id,
                        required,
                        fullWidth: true,
                        variant: "outlined",
                        error: !!error,
                        helperText: hideHelpText ? undefined : (error?.message ?? helperText),
                        sx: {
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "black",
                            },
                            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "black",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: "black",
                                borderWidth: "1px",
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: "16px",
                            },
                        },
                    },
                }}
            />
        </div>
    );
};

export default FormDateTimeInput;
